<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue'
import { useRoute } from 'vue-router'
import { string } from 'yup'
import { useField, useForm } from 'vee-validate'
import loading from '@/assets/images/ide/loading.svg'
import check from '@/assets/images/ide/check-circle.svg'
import { IDEVIEWMODELS } from '@/utils/models'
import { ADVANCEDIDETYPE } from '@/utils/ide'
import settingService from '@/services/ide/settings/setting.service'
import projectsService from '@/services/ide/projects.service'
import ideService from '@/services/ide/ide.service'
import liveCodingService from '@/services/ide/liveCoding.service'
import { useIdeStore } from '@/stores/ide.store'
import { useLiveCodeStore } from '@/stores/liveCode.store'
import { useAdvanceCompilerStore } from '@/stores/advanceCompiler.store'
import ideModal from '@/components/ide/ide/IdeModal.vue'
import Button from '@/components/atoms/button/index.vue'
import BaseInput from '@/components/forms/BaseInput.vue'

const ideStore = useIdeStore()
const advCompilerStore = useAdvanceCompilerStore()
const route = useRoute()
const liveCodeStore = useLiveCodeStore()

const httpError = ref<string>('')
const httpErrorTimeOut = ref<NodeJS.Timeout | number | null>(null)
const httpSuccess = ref<string>('')
const httpSuccessTimeOut = ref<NodeJS.Timeout | number | null>(null)
const httpInProgress = ref<boolean>(false)

const { errors, handleSubmit, resetForm } = useForm()
const { value: projectName, errorMessage: projectNameError } = useField(
  'projectName',
  string().required()
)

const isHttpInProgress = computed(() => {
  return httpInProgress.value
})
const canSubmit = computed(() => {
  return Object.keys(errors.value).length === 0
})

/**
 * @description - To generate the project name
 */
const generateProjectName = () => {
  if (!projectName.value || projectName.value === '') {
    const generatedName = projectsService.generateProjectName()
    projectName.value = generatedName
  }
}

/**
 * Asynchronously saves the project using the provided values.
 * This function calls the `saveProjectActual` method from the `projectsService` with the given values.
 * @param  values - The project name to be saved.
 */
const saveProject = async (values: string) => {
  await projectsService
    .saveProjectActual(true, values, true, true)
    .then(async (response) => {
      if (response?.status === 200) {
        httpSuccess.value = `Project Saved as ${values} Successfully`
      }
      if (liveCodeStore.isLiveCodingActive) {
        liveCodingService.disconnectLiveCoding()
      }
      if (ideStore.isAdvanced) await ideService.initAdvancedIde(ADVANCEDIDETYPE.OPEN)
    })
    .catch((err) => {
      httpError.value = err.response.data.message ? err.response.data.message : err.message
    })
}

const onSubmit = handleSubmit(async (values) => {
  httpInProgress.value = true
  httpError.value = ''
  httpSuccess.value = ''
  await projectsService
    .checkForDubplicateProjectName(values.projectName)
    .then(async () => {
      if (ideStore.isAdvanced && ideStore.isAdvancedCompiler) {
        advCompilerStore.setProjectKeyAdvCompiler(null)
        await ideService.initAdvancedIde(ADVANCEDIDETYPE.INIT, true).then(async () => {
          await saveProject(values?.projectName || '')
        })
      } else {
        await saveProject(values?.projectName || '')
      }
    })
    .catch((err) => {
      httpError.value = err.response.data.message ? err.response.data.message : err.message
    })
    .finally(() => {
      httpInProgress.value = false
      resetForm()
    })
})

onMounted(() => {
  watch(route, () => {
    httpError.value = ''
    httpSuccess.value = ''
  })
  watch(httpError, () => {
    if (httpError.value) {
      if (httpErrorTimeOut.value) clearTimeout(httpErrorTimeOut.value)
      httpErrorTimeOut.value = setTimeout(() => {
        httpError.value = ''
      }, 3000)
    }
  })
  watch(httpSuccess, () => {
    if (httpSuccess.value) {
      if (httpSuccessTimeOut.value) clearTimeout(httpSuccessTimeOut.value)
      httpSuccessTimeOut.value = setTimeout(() => {
        httpSuccess.value = ''
        settingService.closePopup(IDEVIEWMODELS.SAVEAS)
      }, 3000)
    }
  })
})

watch(
  () => ideStore.isSettingPopupHs,
  (val) => {
    if (val) generateProjectName()
  }
)
</script>

<template>
  <ideModal title="Fork project" class="sm:mt-[8vh]">
    <div class="flex h-full flex-col gap-2">
      <div class="flex h-32 items-center justify-center gap-1" :class="{ hidden: !httpSuccess }">
        <img :src="check" alt="" class="h-4 w-4" />
        <p class="text-midnightBlue mb-0.5 text-center text-xs">
          {{ httpSuccess }}
        </p>
      </div>
      <div
        class="flex h-32 items-center justify-center gap-1"
        :class="{ hidden: !isHttpInProgress }"
      >
        <p class="text-midnightBlue text-center text-xs">Saving your project, Please wait...</p>
        <img :src="loading" alt="" class="h-8 w-8" />
      </div>

      <form
        v-if="!httpSuccess && !isHttpInProgress"
        @submit.prevent="onSubmit"
        class="flex h-full flex-col gap-2"
      >
        <BaseInput
          id="save-project"
          label="Project name (auto generated)"
          placeholder="Project Name"
          class="project-name sm:min-w-[40vw] sm:max-w-[50vw]"
          :ideModal="true"
          :error="projectNameError"
          v-model="projectName"
          :isSmallLabel="true"
          :isContactus="true"
        />
        <div class="flex items-center justify-center" :class="{ hidden: !httpError }">
          <p class="error text-center text-xs">
            {{ httpError }}
          </p>
        </div>
        <div class="flex w-full justify-center gap-2 sm:justify-end">
          <Button :disabled="!canSubmit || isHttpInProgress" variant="filled" type="submit">
            Fork project
          </Button>
        </div>
      </form>
    </div>
  </ideModal>
</template>

<style>
.project-name input {
  padding-left: 1em;
  padding-top: 0.75em;
  padding-bottom: 0.75em;
  margin-bottom: 0;
  @apply rounded-md outline-none;
}
</style>
